const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const canonicalizeAbsolutePath = (value: string): string | null => {
  const normalized = value.replace(/\\/g, '/');
  if (!normalized.startsWith('/')) return null;

  const segments: string[] = [];
  for (const segment of normalized.split('/')) {
    if (!segment || segment === '.') continue;
    if (segment === '..') {
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  return `/${segments.join('/')}`;
};

const stripMarkdownDestinationWrapper = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.startsWith('<')) {
    const closing = trimmed.indexOf('>');
    if (closing > 0) return trimmed.slice(1, closing).trim();
  }

  // A quoted Markdown title is not part of the destination.
  return trimmed.replace(/\s+(?:"[^"]*"|'[^']*'|\([^)]*\))\s*$/, '').trim();
};

const findMarkdownDestinationEnd = (input: string, start: number): number => {
  let depth = 1;
  let inAngleDestination = false;
  for (let index = start; index < input.length; index += 1) {
    const char = input[index];
    if (char === '\\') {
      index += 1;
      continue;
    }
    if (char === '<' && index === start) {
      inAngleDestination = true;
      continue;
    }
    if (char === '>' && inAngleDestination) {
      inAngleDestination = false;
      continue;
    }
    if (inAngleDestination) continue;
    if (char === '(') {
      depth += 1;
      continue;
    }
    if (char === ')') {
      depth -= 1;
      if (depth === 0) return index;
      continue;
    }
    if (char === '\n') return -1;
  }
  return -1;
};

/** Extract raw destinations from inline Markdown links and images. */
export const extractMarkdownLinkSources = (markdown: string): string[] => {
  const sources: string[] = [];
  let cursor = 0;

  while (cursor < markdown.length) {
    const openIndex = markdown.indexOf('](', cursor);
    if (openIndex < 0) break;
    const destinationStart = openIndex + 2;
    const destinationEnd = findMarkdownDestinationEnd(markdown, destinationStart);
    if (destinationEnd < 0) {
      cursor = destinationStart;
      continue;
    }

    const source = stripMarkdownDestinationWrapper(
      markdown.slice(destinationStart, destinationEnd),
    );
    if (source) sources.push(source);
    cursor = destinationEnd + 1;
  }

  return sources;
};

/**
 * Resolve a local Markdown destination inside an allowed workspace root.
 * External schemes, network paths and paths escaping the root are rejected.
 */
export const normalizeDeliverableLinkPath = (
  value: string,
  workspaceRoot: string,
): string | null => {
  const source = stripMarkdownDestinationWrapper(value);
  if (!source || source.startsWith('#') || source.startsWith('//')) return null;

  const canonicalRoot = canonicalizeAbsolutePath(workspaceRoot);
  if (!canonicalRoot) return null;

  let pathValue: string;
  if (/^file:\/\//i.test(source)) {
    try {
      const fileUrl = new URL(source);
      if (fileUrl.protocol !== 'file:') return null;
      if (fileUrl.hostname && fileUrl.hostname.toLowerCase() !== 'localhost') return null;
      pathValue = safeDecodeURIComponent(fileUrl.pathname);
    } catch {
      return null;
    }
  } else {
    if (SCHEME_RE.test(source)) return null;
    const withoutSuffix = source.split('#')[0].split('?')[0];
    pathValue = safeDecodeURIComponent(withoutSuffix);
  }

  if (!pathValue || pathValue.includes('\0')) return null;
  pathValue = pathValue.replace(/\\/g, '/');
  const absolutePath = pathValue.startsWith('/')
    ? pathValue
    : `${canonicalRoot}/${pathValue}`;
  const canonicalPath = canonicalizeAbsolutePath(absolutePath);
  if (!canonicalPath) return null;

  if (
    canonicalPath !== canonicalRoot
    && !canonicalPath.startsWith(`${canonicalRoot}/`)
  ) {
    return null;
  }
  return canonicalPath;
};
